import ModalWhatsAppQRCode from "../../components/Modal/ModalWhatsAppQRCode"
import React, { useEffect, useRef, useState } from "react"
import styles from "./whatsAppApiIntegration.module.css"
import Button from '../../components/Form/Button'
import api from '../../services/api'
import { toast } from 'react-toastify'
import { MdHistory, MdOpenInNew, MdOutlineCheck, MdOutlineWhatsapp } from "react-icons/md"
import { AiOutlineDisconnect } from "react-icons/ai"
import FormGroup from '../../components/Form/FormGroup'
import Select from "../../components/Form/Select"
import DatePicker from "../../components/Form/DatePicker"
import Modal from "../../components/Modal"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import Loading from '../layouts/Loading'
import { useData } from "../../contexts/dataContext"
import ModalInbox from "../../components/Modal/ModalInbox"
import Input from './../../components/Form/Input';
import { getNumbersByString, maskPhone } from "../../utils/formatter"
import InboxCard from "../../components/InboxCard"
import BackButton from "../../components/BackButton"
import { useParams } from "react-router-dom"
import TemplatesManager from "./TemplatesManager"
import { useAuth } from "../../contexts/authContext"
import PhoneNumberManager from "./PhoneNumberManager"
import InfoTooltip from "../../components/InfoTooltip"
import { getEmbeddedSignupConfig, parseEmbeddedSignupEvent, WHATSAPP_ONBOARDING_MODES } from "./whatsAppEmbeddedSignup"

const formatNumber = (value = 0) => Number(value || 0).toLocaleString("pt-BR")
const formatDateTime = (date) => date ? new Date(date).toLocaleString("pt-BR") : "-"

const ASSIGNMENT_SOURCE_OPTIONS = [
    { id: "new_conversation", value: "Nova conversa" },
    { id: "manual", value: "Manual" },
    { id: "management", value: "Gestão em massa" },
    { id: "bulk_action", value: "Ação em lote" },
    { id: "outbound_campaign", value: "Campanha de saída" },
    { id: "ai_handoff", value: "IA para humano" },
    { id: "access_request", value: "Liberação de acesso" },
    { id: "chat_start", value: "Início de atendimento" },
]

const ASSIGNMENT_SOURCE_TOOLTIP = "Nova conversa é a atribuição aplicada na chegada do atendimento. Manual é feita por uma pessoa no chat. Gestão em massa usa filtros para alterar conversas. Ação em lote usa conversas selecionadas manualmente. Campanha de saída registra a distribuição da campanha. IA para humano ocorre em transferência do agente. Liberação de acesso registra aprovações de acesso. Início de atendimento registra quando alguém assume ao abrir."

const getDefaultAssignmentFilters = () => ({ dateStart: "", dateEnd: "", sources: [] })
const getWhatsAppApiQuery = () => {
    const query = new URLSearchParams(window.location.search)
    return {
        section: ["accounts", "templates", "profile"].includes(query.get("section")) ? query.get("section") : "accounts",
        inboxId: query.get("inbox_id") || "",
        templateType: query.get("template_type") || "",
    }
}

const WhatsAppApiIntegration = () => {
    const [qrInbox, setQrInbox] = useState(null)
    const initialQuery = getWhatsAppApiQuery()
    const [linkedAccounts, setLinkedAccounts] = useState([])
    const [availableAccounts, setAvailableAccounts] = useState([])
    const [reAuth, setReAuth] = useState(false)
    const [showModalDisconect, setShowModalDisconect] = useState(false)
    const [showModalConfirm, setShowModalConfirm] = useState(false)
    const [showModalAccount, setShowModalAccount] = useState(false)
    const [accountDisconect, setAccountDisconect] = useState({})
    const [accountConect, setAccountConect] = useState({})
    const [accountConfig, setAccountConfig] = useState({})
    const [pin, setPin] = useState("")
    const [users, setUsers] = useState([])
    const [chatBots, setChatBots] = useState([])
    const [hasLoadingAvailable, setHasLoadingAvailable] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [activeSection, setActiveSection] = useState(initialQuery.section)
    const [showAssignmentHistoryModal, setShowAssignmentHistoryModal] = useState(false)
    const [assignmentHistoryInbox, setAssignmentHistoryInbox] = useState({})
    const [assignmentHistory, setAssignmentHistory] = useState({ assignments: [], pagination: {} })
    const [assignmentHistoryFilters, setAssignmentHistoryFilters] = useState(getDefaultAssignmentFilters())
    const [isLoadingAssignmentHistory, setIsLoadingAssignmentHistory] = useState(false)
    const coexistenceAssetIdsRef = useRef(new Set())

    const { teams, roles, allUsersGroup, funnels, loadFunnels } = useData()
    const { permissions } = useAuth()
    const { account_id } = useParams()
    const canAddWhatsAppInbox = permissions?.whatsapp_inboxes >= 2

    useEffect(() => {
        getAllLinkedAccounts()
        getAllChatBots()
        loadFunnels()
    }, [])

    useEffect(() => {
        setUsers(allUsersGroup?.map(user => ({ id: user?._id, value: user?.name, teams: user?.teams?.map(team => team?.team_id || team) || [] })))
    }, [allUsersGroup])

    const getAllLinkedAccounts = async () => {
        try {
            setIsLoading(true)

            const response = await api.get("/integrations/whatsapp-api/accounts/linked")
            const accounts = response?.data?.data || []

            const uniqueAccounts = uniqueByPhoneId(accounts)

            setLinkedAccounts(uniqueAccounts)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const getAllNotLinkedAccounts = async () => {
        try {
            setIsLoading(true)

            const response = await api.get("/integrations/whatsapp-api/accounts/available")
            const accounts = response?.data?.data || []

            const uniqueAvailable = uniqueByPhoneId(accounts).map(account =>
                (coexistenceAssetIdsRef.current.has(`phone:${account?.phone_id}`) || coexistenceAssetIdsRef.current.has(`waba:${account?.waba_id}`))
                    ? { ...account, onboarding_mode: WHATSAPP_ONBOARDING_MODES.COEXISTENCE, is_on_biz_app: true }
                    : account
            )

            const filtered = uniqueAvailable.filter(availableAccount =>
                !linkedAccounts.some(linkedAccount =>
                    linkedAccount?.metadata?.phone_id === availableAccount.phone_id
                )
            )

            setAvailableAccounts(filtered)
            setHasLoadingAvailable(true)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const uniqueByPhoneId = (list = []) => {
        const map = new Map()

        list.forEach(item => {
            const phoneId = item?.metadata?.phone_id || item?.phone_id
            if (phoneId && !map.has(phoneId)) {
                map.set(phoneId, item)
            }
        })

        return Array.from(map.values())
    }

    const handleSaveCode = async (code) => {
        try {
            setIsLoading(true)
            const response = await api.patch("/integrations/whatsapp-api/accessToken", { code })
            setIsLoading(false)
            return response
        } catch (err) {
            setIsLoading(false)
            toast.error(err?.response?.data?.message)
            if (err?.action && err.action === "REAUTH") {
                setReAuth(true)
            }
        }
    }

    const handleCloseModalConfirm = () => {
        setAccountConect({})
        setShowModalConfirm(false)
    }

    const handleShowModalConfirm = (account) => {
        setPin("")
        setAccountConect(account)
        setShowModalConfirm(true)
    }

    const handleConectAccount = (account) => {
        setIsLoading(true)
        api.post("/integrations/whatsapp-api/connectAccount", account).then(response => {
            const newAccount = response?.data?.data
            if (newAccount) {
                setLinkedAccounts(previousValue => {
                    return [...previousValue, newAccount]
                })
                setAvailableAccounts(previousValue => previousValue?.filter(el => newAccount?.metadata?.phone_id === el?.metadata?.phone_id))
            }
            handleCloseModalConfirm()
            handleShowModalAccount(newAccount._id)
            setIsLoading(false)
            toast.success(response?.data?.message)
        }).catch(err => {
            setIsLoading(false)
            toast.error(err?.response?.data?.message)
        })
    }

    const handleCloseModalDisconect = () => {
        setAccountDisconect({})
        setShowModalDisconect(false)
    }

    const handleShowModalDisconect = (account) => {
        setAccountDisconect(account)
        setShowModalDisconect(true)
    }

    const handleDisconectAccount = (account) => {
        setIsLoading(true)
        api.post("/integrations/whatsapp-api/disconnectAccount", { inbox_id: account._id }).then(response => {
            setLinkedAccounts(previousValue => previousValue?.filter(el => el._id !== account._id))
            handleCloseModalDisconect()
            setIsLoading(false)
            setHasLoadingAvailable(false)
            toast.success(response?.data?.message)
        }).catch(err => {
            setIsLoading(false)
            toast.error(err?.response?.data?.message)
        })
    }

    const handleShowModalAccount = (accountId) => {
        setIsLoading(true)
        api.get(`/integrations/whatsapp-api/accounts/${accountId}`).then(response => {
            const account = response?.data?.data || {}
            setAccountConfig(account)
            setShowModalAccount(true)
            setIsLoading(false)
        }).catch(err => {
            setIsLoading(false)
            toast.error(err?.response?.data?.message)
        })
    }

    const handleCloseModalAccount = () => {
        setAccountConect({})
        setShowModalAccount(false)
    }

    const handleUpdateAccount = (data) => {
        setIsLoading(true)
        api.patch(`/integrations/whatsapp-api/accounts/${data?._id}`, data).then(response => {
            const account = response.data.data
            updateAccountByContent(account)
            handleCloseModalAccount()
        }).catch(err => toast.error(err?.response?.data?.message))
            .finally(() => setIsLoading(false))
    }

    const updateAccountByContent = (newContent) => {
        const inbox_id = newContent?._id
        if (inbox_id) {
            setLinkedAccounts(previousValue => {
                if (previousValue.find(inbox => inbox?._id === inbox_id)) {
                    return previousValue.map(inbox => {
                        if (inbox?._id === inbox_id) {
                            return { ...inbox, ...newContent, metadata: { ...inbox.metadata, ...newContent.metadata } }
                        }
                        return inbox
                    })
                }
                return previousValue
            })
            if (accountConfig?._id === inbox_id) {
                setAccountConfig(previousValue => ({ ...previousValue, ...newContent, metadata: { ...previousValue.metadata, ...newContent.metadata } }))
            }
        }
    }

    const getAllChatBots = () => {
        setIsLoading(true)
        api.get("/integrations/whatsapp/chatbots").then(response => {
            const data = response?.data?.data || []
            setChatBots(data)
        }).catch(err => toast.error(err?.response?.data?.message))
            .finally(() => setIsLoading(false))
    }

    const handleLoginBM = (onboardingMode = WHATSAPP_ONBOARDING_MODES.CLOUD_API) => {
        if (!window.initializeFacebookSDK) {
            toast.error("Não foi possível iniciar o processo de conexão! Tente novamente em breve.")
            return
        }
        window.initializeFacebookSDK("1414546936407322")

        const configLogin = getEmbeddedSignupConfig('1129448555659979', onboardingMode, reAuth)
        const sessionLogger = (event) => {
            const session = parseEmbeddedSignupEvent(event)
            if (!session) return

            if (session.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
                if (session?.data?.phone_number_id) {
                    coexistenceAssetIdsRef.current.add(`phone:${session.data.phone_number_id}`)
                } else if (session?.data?.waba_id) {
                    coexistenceAssetIdsRef.current.add(`waba:${session.data.waba_id}`)
                }
                window.setTimeout(() => getAllNotLinkedAccounts(), 500)
            }
        }
        window.addEventListener("message", sessionLogger)

        window.FB.login(function (response) {
            if (response.status === "connected" || response?.authResponse?.code) {
                const newCode = response?.authResponse?.code
                if (newCode) {
                    handleSaveCode(newCode).then(() => {
                        getAllNotLinkedAccounts()
                    })
                }
            }
            window.setTimeout(() => window.removeEventListener("message", sessionLogger), 5000)
        }, configLogin)
    }

    const sortLinkedAccounts = (a, b) => {
        if (a.account_name > b.account_name) {
            return 1
        } else if (a.account_name < b.account_name) {
            return -1
        } else {
            return 0
        }
    }

    const sortAvailableAccounts = (a, b) => {
        if (a.name > b.name) {
            return 1
        } else if (a.name < b.name) {
            return -1
        } else {
            return 0
        }
    }

    const getContentByModalConfirm = () => {
        return (
            <FormGroup>
                <span>Após a conexão, as mensagens enviadas para o número de WhatsApp serão direcionadas automáticamente para nossa plataforma. Na próxima tela será possível definir algumas configurações adicionais!</span>
                <Input onChange={(e) => setPin(getNumbersByString(e.target.value?.substring(0, 6)))} placeholder="Insira o PIN de 6 dígitos para ativação" value={pin} />
            </FormGroup>
        )
    }

    const getAssignmentHistory = (inboxId = assignmentHistoryInbox?._id, filters = assignmentHistoryFilters, page = 1) => {
        if (!inboxId) return

        setIsLoadingAssignmentHistory(true)
        api.get(`/integrations/whatsapp-api/accounts/${inboxId}/assignment-history`, {
            params: {
                ...(filters.dateStart ? { dateStart: filters.dateStart } : {}),
                ...(filters.dateEnd ? { dateEnd: filters.dateEnd } : {}),
                ...(filters.sources?.length ? { sources: filters.sources.join(",") } : {}),
                page,
                limit: 50,
            }
        }).then(response => {
            const data = response?.data?.data || { assignments: [], pagination: {} }
            setAssignmentHistory(previousValue => ({
                assignments: page > 1 ? [...(previousValue?.assignments || []), ...(data.assignments || [])] : data.assignments || [],
                pagination: data.pagination || {}
            }))
        }).catch(err => toast.error(err?.response?.data?.message))
            .finally(() => setIsLoadingAssignmentHistory(false))
    }

    const handleShowAssignmentHistory = (inbox) => {
        const filters = getDefaultAssignmentFilters()
        setAssignmentHistoryInbox(inbox)
        setAssignmentHistoryFilters(filters)
        setShowAssignmentHistoryModal(true)
        getAssignmentHistory(inbox?._id, filters)
    }

    const handleCloseAssignmentHistory = () => {
        setShowAssignmentHistoryModal(false)
        setAssignmentHistoryInbox({})
        setAssignmentHistory({ assignments: [], pagination: {} })
    }

    const handleAssignmentHistoryPeriodChange = (dateStart, dateEnd) => {
        setAssignmentHistoryFilters(previousValue => ({ ...previousValue, dateStart: dateStart || "", dateEnd: dateEnd || "" }))
    }

    const handleAssignmentHistorySourceChange = (sources) => {
        setAssignmentHistoryFilters(previousValue => ({ ...previousValue, sources: sources || [] }))
    }

    const handleLoadMoreAssignmentHistory = () => {
        const nextPage = Number(assignmentHistory?.pagination?.page || 1) + 1
        getAssignmentHistory(assignmentHistoryInbox?._id, assignmentHistoryFilters, nextPage)
    }

    const getAssignmentSourceLabel = (source) => {
        const labels = {
            new_conversation: "Nova conversa",
            manual: "Manual",
            ai_handoff: "IA para humano",
            management: "Gestão em massa",
            bulk_action: "Ação em lote",
            outbound_campaign: "Campanha de saída",
            access_request: "Liberação de acesso",
            chat_start: "Início de atendimento",
        }

        return labels[source] || "Atribuição"
    }

    const getAssignmentRuleLabel = (assignment) => {
        const rule = assignment?.assignment_rule
        const owner = assignment?.owner

        if (rule?.users?.includes("auto") && rule?.round_robin?.mode === "custom") return "Rodízio personalizado"
        if (rule?.users?.includes("auto") || assignment?.round_robin_selected_user_id) return "Rodízio automático"
        if (rule?.assign_to === "teamAndRole" || owner?.assignTo === "teamAndRole") return "Equipe/Cargo"
        if (rule?.users?.length || owner?.assignTo === "users") return "Usuário(s)"

        return "Regra não registrada"
    }

    const getAssignmentReasonLabel = (assignment) => {
        const context = assignment?.assignment_context
        const reasons = {
            lead_owner_match: "Responsável do lead correspondente",
            inbox_default_rule: "Regra padrão da caixa",
        }

        if (context?.reason_label) return context.reason_label
        if (context?.reason && reasons[context.reason]) return reasons[context.reason]
        if (assignment?.source === "new_conversation") return `Regra padrão: ${getAssignmentRuleLabel(assignment)}`
        if (assignment?.source === "manual") return "Atribuição manual"
        if (assignment?.source === "management") return "Alteração em massa"
        if (assignment?.source === "bulk_action") return "Ação em lote"
        if (assignment?.source === "outbound_campaign") return "Distribuição de campanha"
        if (assignment?.source === "ai_handoff") return "Transferência da IA"
        if (assignment?.source === "access_request") return "Solicitação aprovada"
        if (assignment?.source === "chat_start") return "Usuário assumiu o atendimento"

        return getAssignmentRuleLabel(assignment)
    }

    const getAssignmentTargetLabel = (assignment) => {
        const users = assignment?.assigned_users?.map(user => user.name).filter(Boolean) || []
        const team = assignment?.team?.name
        const role = assignment?.role?.name

        if (users.length) return users.join(", ")
        if (team && role) return `${team} / ${role}`
        if (team) return team
        if (role) return role

        return "Destino não registrado"
    }

    const getAssignmentEligibleLabel = (assignment) => {
        const users = assignment?.eligible_users?.map(user => user.name).filter(Boolean) || []
        return users.length ? users.join(", ") : "-"
    }

    return (
        <div className={styles.container}>
            <BackButton fallback={`/${account_id}/integrations/chat`} />
            <div className={styles.content}>
                <div className={`${styles.card} ${activeSection === "templates" || activeSection === "profile" ? styles.templatesCard : ""}`}>
                    <div className={styles.header}>
                        <MdOutlineWhatsapp />
                        <span>WhatsApp API</span>
                        {activeSection === "accounts" && canAddWhatsAppInbox &&
                            <FormGroup>
                                <Button variant="primary" onClick={() => handleLoginBM(WHATSAPP_ONBOARDING_MODES.CLOUD_API)}>
                                    Nova conta Cloud API
                                </Button>
                                <Button variant="secondary" onClick={() => handleLoginBM(WHATSAPP_ONBOARDING_MODES.COEXISTENCE)}>
                                    Conectar WhatsApp Business
                                </Button>
                            </FormGroup>
                        }
                    </div>
                    <div className={styles.sections}>
                        <button className={activeSection === "accounts" ? styles.active : ""} onClick={() => setActiveSection("accounts")}>
                            Contas
                        </button>
                        <button className={activeSection === "templates" ? styles.active : ""} onClick={() => setActiveSection("templates")}>
                            Templates
                        </button>
                        <button className={activeSection === "profile" ? styles.active : ""} onClick={() => setActiveSection("profile")}>
                            Perfil
                        </button>
                    </div>
                    <div className={styles.body}>
                        {activeSection === "accounts" && <>
                            <FormGroup labelText="Contas Conectadas">
                                {linkedAccounts.length > 0 && linkedAccounts.sort(sortLinkedAccounts).map(account => (
                                    <InboxCard
                                        key={account._id}
                                        name={`${account.name}${account?.metadata?.onboarding_mode === WHATSAPP_ONBOARDING_MODES.COEXISTENCE ? " · Coexistência" : ""}`}
                                        phone={maskPhone(account?.metadata?.phone_number)}
                                        status={account.status}
                                        platform="whatsapp_api"
                                        actions={[
                                            {
                                                label: "Histórico de atribuição",
                                                onClick: () => handleShowAssignmentHistory(account),
                                                alwaysVisible: true
                                            },
                                            { label: "QR Code de divulgação", onClick: () => setQrInbox(account) },
                                            ...(canAddWhatsAppInbox ? [
                                            {
                                                label: "Desconectar",
                                                type: "danger",
                                                onClick: () => handleShowModalDisconect(account)
                                            }
                                            ] : []),
                                        ]}
                                        rightButton={{
                                            label: "Configurar",
                                            onClick: () => handleShowModalAccount(account._id)
                                        }}
                                    />
                                ))}
                                {linkedAccounts.length === 0 && <span>Nenhuma conta conectada até o momento!</span>}
                            </FormGroup>
                            {canAddWhatsAppInbox &&
                                <FormGroup labelText="Contas Disponíveis (Não Conectadas)">
                                    {availableAccounts.length > 0 && availableAccounts.sort(sortAvailableAccounts).map((account, index) => (
                                        <InboxCard
                                            key={index}
                                            name={account.name}
                                            phone={maskPhone(account.phone_number)}
                                            status={account.status}
                                            platform="whatsapp_api"
                                            rightButton={{
                                                label: account?.onboarding_mode === WHATSAPP_ONBOARDING_MODES.COEXISTENCE ? "Conectar em coexistência" : "Conectar",
                                                type: "primary",
                                                onClick: () => handleShowModalConfirm(account)
                                            }}
                                        />
                                    ))}
                                    {hasLoadingAvailable && availableAccounts.length === 0 && <span>Nenhuma conta disponível para conexão!</span>}
                                    {!hasLoadingAvailable && <Button variant="primary" onClick={getAllNotLinkedAccounts}>Listar contas disponíveis</Button>}
                                </FormGroup>
                            }
                        </>}
                        {activeSection === "templates" &&
                            <TemplatesManager
                                linkedAccounts={linkedAccounts}
                                setIsLoading={setIsLoading}
                                initialInboxId={initialQuery.inboxId}
                                initialTemplateType={initialQuery.templateType}
                            />
                        }
                        {activeSection === "profile" &&
                            <PhoneNumberManager
                                linkedAccounts={linkedAccounts}
                                setIsLoading={setIsLoading}
                                onUpdateAccount={updateAccountByContent}
                                initialInboxId={initialQuery.inboxId}
                            />
                        }
                    </div>
                </div>
            </div>
            {canAddWhatsAppInbox && showModalConfirm &&
                <ModalConfirm
                    closeModal={handleCloseModalConfirm}
                    icon={<MdOutlineCheck />}
                    title="Deseja realmente conectar a conta?"
                    description={`Ao conectar a conta ${accountConect.name} você concorda com os detalhes abaixo`}
                    text={accountConect?.onboarding_mode === WHATSAPP_ONBOARDING_MODES.COEXISTENCE
                        ? "O mesmo número continuará disponível no aplicativo WhatsApp Business. Contatos e histórico serão sincronizados se o compartilhamento tiver sido autorizado durante a conexão."
                        : getContentByModalConfirm()
                    }
                    onSubmit={() => handleConectAccount({ ...accountConect, pin: accountConect?.onboarding_mode === WHATSAPP_ONBOARDING_MODES.COEXISTENCE ? undefined : pin })}
                />
            }
            {qrInbox && <ModalWhatsAppQRCode inbox={qrInbox} closeModal={() => setQrInbox(null)} />}
            {canAddWhatsAppInbox && showModalDisconect &&
                <ModalConfirm
                    closeModal={handleCloseModalDisconect}
                    icon={<AiOutlineDisconnect />}
                    title="Deseja realmente desconectar a conta?"
                    description={`Ao desconectar a conta ${accountDisconect.account_name} você concorda com os detalhes abaixo`}
                    text="Após a desconexão, as mensagens enviadas a essa conta deixarão de ser direcionadas para nossa plataforma."
                    onSubmit={() => handleDisconectAccount(accountDisconect)}
                />
            }
            {showModalAccount &&
                <ModalInbox
                    inbox={accountConfig}
                    teams={teams}
                    roles={roles}
                    users={users}
                    usersWithAuto={[{ id: "auto", value: "Rodízio Automático" }, ...users]}
                    chatBots={chatBots}
                    funnels={funnels}
                    closeModal={handleCloseModalAccount}
                    onSubmit={handleUpdateAccount}
                />
            }
            {showAssignmentHistoryModal &&
                <Modal
                    icon={<MdHistory />}
                    title="Histórico de atribuição"
                    description={`Caixa de entrada ${assignmentHistoryInbox?.name || ""}`}
                    closeModal={handleCloseAssignmentHistory}
                    fullWidith={true}
                    heightFitContent={true}
                >
                    <div className={styles.assignmentHistoryModal}>
                        <div className={styles.assignmentHistoryFilters}>
                            <DatePicker
                                multiple={true}
                                name="Período"
                                dateStart={assignmentHistoryFilters.dateStart || ""}
                                dateEnd={assignmentHistoryFilters.dateEnd || ""}
                                onChange={handleAssignmentHistoryPeriodChange}
                            />
                            <div className={styles.assignmentHistorySourceFilter}>
                                <div>
                                    <span>Origem</span>
                                    <InfoTooltip text={ASSIGNMENT_SOURCE_TOOLTIP} preferredPosition="bottom" />
                                </div>
                                <Select
                                    name="Todas as origens"
                                    placeholder="Todas as origens"
                                    options={ASSIGNMENT_SOURCE_OPTIONS}
                                    selected={assignmentHistoryFilters.sources || []}
                                    multiple={true}
                                    onChange={handleAssignmentHistorySourceChange}
                                />
                            </div>
                            <Button variant="secondary" onClick={() => getAssignmentHistory()}>
                                Filtrar
                            </Button>
                        </div>
                        <div className={styles.assignmentHistorySummary}>
                            <strong>{formatNumber(assignmentHistory?.pagination?.total || assignmentHistory?.assignments?.length || 0)}</strong>
                            <span>atribuições encontradas no período selecionado</span>
                        </div>
                        <div className={styles.assignmentHistoryTable}>
                            <div className={styles.assignmentHistoryHeader}>
                                <span>Data</span>
                                <span>Destino</span>
                                <span>Motivo</span>
                                <span>Regra</span>
                                <span>Elegíveis</span>
                                <span>Origem</span>
                                <span>Conversa</span>
                            </div>
                            {assignmentHistory?.assignments?.map(assignment => (
                                <div key={assignment._id} className={styles.assignmentHistoryRow}>
                                    <span>{formatDateTime(assignment.created_at)}</span>
                                    <strong>{getAssignmentTargetLabel(assignment)}</strong>
                                    <span>{getAssignmentReasonLabel(assignment)}</span>
                                    <span>{getAssignmentRuleLabel(assignment)}</span>
                                    <span>{getAssignmentEligibleLabel(assignment)}</span>
                                    <span>{getAssignmentSourceLabel(assignment.source)}</span>
                                    <span>
                                        {assignment?.conversation?.url ?
                                            <a href={`/${account_id}/chat/${assignment.conversation._id}`} target="_blank" rel="noreferrer">
                                                Abrir <MdOpenInNew />
                                            </a>
                                            : "-"
                                        }
                                    </span>
                                </div>
                            ))}
                            {!assignmentHistory?.assignments?.length && !isLoadingAssignmentHistory &&
                                <div className={styles.assignmentHistoryEmpty}>
                                    Nenhuma atribuição registrada nesse período.
                                </div>
                            }
                            {isLoadingAssignmentHistory &&
                                <div className={styles.assignmentHistoryEmpty}>
                                    Carregando histórico...
                                </div>
                            }
                        </div>
                        {assignmentHistory?.pagination?.has_more &&
                            <div className={styles.assignmentHistoryLoadMore}>
                                <Button variant="secondary" onClick={handleLoadMoreAssignmentHistory} disabled={isLoadingAssignmentHistory}>
                                    {isLoadingAssignmentHistory ? "Carregando..." : "Carregar mais"}
                                </Button>
                            </div>
                        }
                    </div>
                </Modal>
            }
            {isLoading && <Loading fullAccount={true} />}
        </div>
    )
}

export default WhatsAppApiIntegration
